import { roundToTwoDecimals } from '@fruitful/fruitils';
import { type TaxFiling } from '@fruitful/types';

import { FEDERAL_TAX_BRACKETS } from './assumptions/federalTaxBrackets';
import { STANDARD_DEDUCTIONS } from './assumptions/standardDeductions';
import { STATE_TAX_RATES } from './assumptions/stateTaxRates';

/**
 * Calculates the effective tax rate for income that is taxed at the ordinary income rate.
 */
export class EffectiveTaxRateCalculator {
    private readonly agi: number;
    private readonly taxableIncome: number;

    constructor(
        private readonly taxableSocialSecurity: number,
        private readonly taxableDistributions: number,
        private readonly residentialLocation: string,
        private readonly taxFilingStatus: TaxFiling,
    ) {
        const standardDeduction = STANDARD_DEDUCTIONS[this.taxFilingStatus];

        // Reasonably estimate agi, assuming no other sources of income.
        this.agi = this.taxableSocialSecurity + this.taxableDistributions;
        this.taxableIncome = roundToTwoDecimals(
            Math.max(0, this.agi - standardDeduction),
        );
    }

    /**
     * Calculate effective tax rate (average tax rate)
     * @returns Effective tax rate as a decimal
     */
    public calculate(): number {
        if (this.taxableIncome <= 0) {
            return 0;
        }

        const totalTaxLiability = this.calculateTotalTaxLiability();
        return roundToTwoDecimals(totalTaxLiability / this.agi); // Use agi as the denominator as its supposedly most commonly used for effective tax rate calculations and appropriately handles logic for applying the output effective tax rate back to the taxable social security and distributions.
    }

    /**
     * Calculate total tax liability (federal + state)
     * @returns Total tax liability amount
     */
    private calculateTotalTaxLiability(): number {
        const federalTax = this.calculateFederalTaxLiability();
        const stateTax = this.calculateStateTaxLiability();
        return roundToTwoDecimals(federalTax + stateTax);
    }

    /**
     * Calculate federal tax liability using progressive brackets
     * @returns Federal tax liability amount
     */
    private calculateFederalTaxLiability(): number {
        const brackets = FEDERAL_TAX_BRACKETS[this.taxFilingStatus];
        let taxLiability = 0;
        const taxableIncome = this.taxableIncome;

        for (let i = 0; i < brackets.length; i++) {
            const bracket = brackets[i];
            if (!bracket) continue;

            const nextBracket = brackets[i + 1];
            const bracketThreshold = bracket.threshold;
            const nextBracketThreshold = nextBracket?.threshold ?? Infinity;

            if (taxableIncome <= bracketThreshold) {
                break;
            }

            const taxableInBracket = Math.min(
                taxableIncome - bracketThreshold,
                nextBracketThreshold - bracketThreshold,
            );

            if (taxableInBracket > 0) {
                taxLiability += taxableInBracket * bracket.rate;
            }
        }

        return roundToTwoDecimals(taxLiability);
    }

    /**
     * Calculate state tax liability
     * @returns State tax liability amount
     */
    private calculateStateTaxLiability(): number {
        const stateCode = this.residentialLocation.toUpperCase();
        const stateRate = STATE_TAX_RATES[stateCode];

        if (stateRate === undefined || stateRate === 0) {
            return 0;
        }

        return roundToTwoDecimals(this.taxableIncome * stateRate);
    }
}
